import React from "react";
import PropTypes from "prop-types";
import track from "lib/tracking/track";
import { buildScopeTranslate } from "lib/I18n";
import debounce from "lodash/debounce";
import { SCREENS, LOCKED_STATES, ATTEMPT_CONFLICT_CODES, ATTEMPT_STATUS, MS_PER_SECOND, PREVIEW_TYPES } from "./constants";
import {
  gradingErrorChanged,
  gradingErrorFromAttempt,
  gradingErrorToast,
  isHeldError,
  isHeldWait,
  retryWaitSeconds,
  routesAwayFromError,
} from "./gradingError";
import {
  assessmentAttemptHref,
  assessmentResultHref,
  fetchFromHref,
  getAssessmentAttempt,
  getPresentation,
  startAttempt,
  submitResults,
} from "./apiClient";
import {
  buildErrorResponses,
  buildSubmitResponses,
  deriveResultSummary,
  getResultResponses,
  isGradedResultPayload,
} from "./resultUtils";
import { assessmentCopyKey, isSaqKnowledgeCheck } from "./utils";
import AssessmentFrameworkTopBar from "./components/AssessmentFrameworkTopBar";
import AssessmentFrameworkIntroScreen from "./components/AssessmentFrameworkIntroScreen";
import AssessmentFrameworkQuestionScreen from "./components/AssessmentFrameworkQuestionScreen";
import AssessmentFrameworkResultsScreen from "./components/AssessmentFrameworkResultsScreen";
import LeaveAssessmentModal from "./components/LeaveAssessmentModal";
import AwaitingEvaluationScreen from "./components/AwaitingEvaluationScreen";
import LoadingScreen from "./components/LoadingScreen";
import ErrorScreen from "./components/ErrorScreen";
import GradingErrorScreen from "./components/GradingErrorScreen";
import { clearDraft, loadDraft, saveDraft } from "./storageUtils";

const ASSESSMENT_FRAMEWORK_SCOPE = "views.content.assessment_framework.assessments";

const tCommon = buildScopeTranslate(`${ASSESSMENT_FRAMEWORK_SCOPE}.common`);
const tErrors = buildScopeTranslate(`${ASSESSMENT_FRAMEWORK_SCOPE}.errors`);
const tNav = buildScopeTranslate(`${ASSESSMENT_FRAMEWORK_SCOPE}.pages.top_bar`);
const tAssessment = buildScopeTranslate(ASSESSMENT_FRAMEWORK_SCOPE);
const ASSESSMENT_COMPLETION_TOAST_STORAGE_KEY = "assessment_framework_completion_toast";

const SCREEN_DOCUMENT_TITLE_KEYS = {
  [SCREENS.INTRO]: "document_title_intro",
  [SCREENS.QUESTION]: "document_title_question",
  [SCREENS.AWAITING_EVALUATION]: "document_title_awaiting",
  [SCREENS.RESULTS]: "document_title_results",
};

// The backend owns the poll interval. This cap only changes the awaiting copy
// once scoring has run long; it does not stop the loop.
const MAX_POLL_DURATION_MS = 4 * 60 * 1000;
const NETWORK_RETRY_LIMIT = 3;
const NETWORK_RETRY_BASE_MS = 1000;
// setTimeout runs a longer delay immediately, which would turn the held wait into a loop.
const MAX_TIMER_DELAY_MS = 2 ** 31 - 1;
const NETWORK_RETRY_JITTER_MS = 500;

const ERROR_CODES = {
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  REQUEST_TIMEOUT: 408,
  CONFLICT: 409,
  TOO_MANY_REQUESTS: 429,
  INTERNAL_SERVER_ERROR: 500
}

const RECOVERY_ACTIONS = {
  QUESTIONS: "questions",
  AWAITING: "awaiting",
  RESULTS: "results",
  GRADING_ERROR: "grading_error",
};

function pollDelayMs(retryAfterSeconds) {
  const seconds = Number(retryAfterSeconds);
  if (!Number.isFinite(seconds) || seconds <= 0) {
    return null;
  }

  return seconds * 1000;
}

// Transport failures have no status. 408, 429, and 5xx are transient.
// Other HTTP statuses stop the loop instead of being reported as a lost connection.
function isRetryablePollError(error) {
  const status = error?.railsFetchStatusCode;
  if (typeof status !== "number") return true;

  return status === ERROR_CODES.REQUEST_TIMEOUT || status === ERROR_CODES.TOO_MANY_REQUESTS || status >= ERROR_CODES.INTERNAL_SERVER_ERROR;
}

function isValidPoll(poll) {
  return Boolean(poll?.href) && pollDelayMs(poll?.retry_after_seconds) != null;
}

function hasPollTarget(attempt) {
  return isValidPoll(attempt?.poll);
}

// The attempt read on reload carries the poll. The result read carries the
// answers. A grading result that omits poll must keep the attempt's, or the
// cold resume treats "nothing to poll" as failure and opens the intro.
function withAttemptPoll(result, attempt) {
  if (
    hasPollTarget(result)
    || getAttemptStatus(result) === ATTEMPT_STATUS.GRADED
    || !hasPollTarget(attempt)
  ) {
    return result;
  }

  return { ...result, poll: attempt.poll };
}

// While an attempt grades, the results screen can already list its answers: the graded
// MCQs, and the SAQs still pending. If grading then fails they carry over to the error
// screen as they are, so a pending SAQ's spinner turns into its warning in place instead
// of the learner passing through a loading screen.
const answersOnScreen = ({ result }) => result?.responses || null;

  
function recoveryForAttempt(attempt) {
  const status = getAttemptStatus(attempt);
  if (status === ATTEMPT_STATUS.IN_PROGRESS) {
    return { action: RECOVERY_ACTIONS.QUESTIONS, attempt };
  }

  if (status === ATTEMPT_STATUS.AWAITING_EVALUATION && hasPollTarget(attempt)) {
    return { action: RECOVERY_ACTIONS.AWAITING, attempt };
  }

  if (status === ATTEMPT_STATUS.ERROR) {
    return { action: RECOVERY_ACTIONS.GRADING_ERROR, attempt };
  }

  if (isGradedResultPayload(attempt) && attempt.result_href) {
    return { action: RECOVERY_ACTIONS.RESULTS, attempt };
  }

  return null;
}

// The server's view of an attempt that was submitted and is still grading or
// failed grading. One still grading is present even once attempts are used up,
// since the submit counted it; one that failed grading is refunded and present
// only while the learner can still re-grade it.
function getServerRecovery(presentation) {
  const recovery = recoveryForAttempt(presentation?.learner_context?.current_attempt);
  const action = recovery?.action;
  if (action === RECOVERY_ACTIONS.AWAITING) {
    return recovery;
  }

  // The server only reports a retryable error here. If it ever sent one with nothing to
  // retry, recovering into it would route straight back to this reload, forever.
  if (action === RECOVERY_ACTIONS.GRADING_ERROR && !routesAwayFromError(recovery.attempt)) {
    return recovery;
  }

  return null;
}

function isAttemptsExhausted(presentation) {
  const maxAttempts = presentation?.max_attempts;
  const attemptCount = presentation?.learner_context?.attempt_count || 0;

  return (
    presentation?.learner_context?.passed !== true &&
    maxAttempts != null &&
    attemptCount >= maxAttempts
  );
}

function getLockedState(presentation) {
  if (presentation?.learner_context?.passed === true) {
    return LOCKED_STATES.COMPLETE;
  }

  if (isAttemptsExhausted(presentation)) {
    return LOCKED_STATES.EXHAUSTED;
  }

  return null;
}

function getAttemptId(attempt) {
  return attempt?.attempt_id || attempt?.id || null;
}

function getAttemptNumber(attempt) {
  return attempt?.attempt_number ?? null;
}

function getAttemptStatus(attempt) {
  return attempt?.status || null;
}

function getKnowledgeChecks(attempt) {
  return attempt?.knowledge_checks || [];
}

const ANSWER_SIDE_EFFECT_DEBOUNCE_MS = 300;

// Keep only the saved responses that still apply to the given attempt: the knowledge
// check must exist, and for choice-based (MCQ) checks the saved value must still match
// a live choice id — a since-removed choice would otherwise submit as an answer that
// nothing visibly selects. Choice-less checks (open-ended text) have no ids to match,
// so their value passes through.
function filterAnswersForAttempt(responses, attempt) {
  const choiceIdsByCheck = new Map(
    getKnowledgeChecks(attempt).map((knowledgeCheck) => [
      knowledgeCheck.id,
      (knowledgeCheck.body?.choices || []).map((choice) => choice.id),
    ])
  );

  return Object.fromEntries(
    Object.entries(responses).filter(([knowledgeCheckId, value]) => {
      const choiceIds = choiceIdsByCheck.get(knowledgeCheckId);
      if (!choiceIds) return false;
      return choiceIds.length === 0 || choiceIds.includes(value);
    })
  );
}

function getDocumentTitle(screen, presentation) {
  if (screen === SCREENS.GRADING_ERROR) {
    return tCommon("document_title_error");
  }

  const titleKey = SCREEN_DOCUMENT_TITLE_KEYS[screen];

  if (!titleKey) {
    return tCommon("document_title_suffix");
  }

  return tCommon(titleKey, { title: presentation.title });
}

function getErrorMessage(error) {
  if (error?.railsFetchStatusCode === ERROR_CODES.UNAUTHORIZED) {
    return tErrors("unauthorized");
  }

  return error?.error || error?.message || error?.railsFetchError || tErrors("unexpected_error");
}

function getSubmitErrorMessage(error) {
  if (error?.railsFetchStatusCode === ERROR_CODES.UNAUTHORIZED) {
    return tErrors("unauthorized");
  }

  return error?.error || error?.message || error?.railsFetchError || tErrors("submit_failed");
}

function deriveAssessmentStatus(screen, isCompleted) {
  if (screen === SCREENS.INTRO) {
    return 'not-started';
  }
  if (isCompleted) {
    return 'complete';
  }
  return 'in-progress';
}

function buildFeedbackAttributes({ attempt, result, presentation }, { userTbid, userEmail, assessmentDefinitionId }, isCompleted, status) {
  return {
    user_tbid: userTbid || null,
    user_email: userEmail || null,
    assessment_id: assessmentDefinitionId || null,
    assessment_instance_id: attempt?.id || null,
    assessment_scoring: presentation?.passing_score ? 'required' : 'suggested',
    status,
    score: isCompleted ? Math.round((result?.score || 0) * 100) : null,
    is_passed: isCompleted ? (result?.passed || false) : false,
    is_complete: isCompleted,
  };
}

function partitionAttributes(attributes) {
  const toSet = {};
  const toRemove = [];

  for (const [key, value] of Object.entries(attributes)) {
    if (value != null) {
      toSet[key] = value;
    } else {
      toRemove.push(key);
    }
  }

  return { toSet, toRemove };
}

function updateSprigAttributes(toSet, toRemove) {
  if (toRemove.length > 0) {
    window.Sprig('removeAttributes', toRemove);
  }
  window.Sprig('setAttributes', toSet);
}

export default class AssessmentFrameworkApp extends React.Component {
  static propTypes = {
    assessmentDefinitionId: PropTypes.string.isRequired,
    learnerId: PropTypes.string,
    returnUrl: PropTypes.string,
    sourceTrailId: PropTypes.string,
    sourceTrailUid: PropTypes.string,
    testOut: PropTypes.bool,
    showUnassignedNotice: PropTypes.bool,
    // Author preview: the trail was viewed in preview mode, so this assessment may be
    // unpublished and its attempts/results must not be persisted. Server-driven.
    previewMode: PropTypes.bool,
    // Learner preview: a preview-permitted author is taking the PUBLISHED assessment
    // as a learner would (pool_config draw). Like previewMode, nothing persists and
    // no toast/analytics fire. Server-driven.
    learnerPreviewMode: PropTypes.bool,
    userTbid: PropTypes.string,
    userEmail: PropTypes.string,
  };

  static defaultProps = {
    learnerId: null,
    returnUrl: null,
    sourceTrailId: null,
    sourceTrailUid: null,
    testOut: null,
    showUnassignedNotice: false,
    previewMode: false,
    learnerPreviewMode: false,
    userTbid: null,
    userEmail: null,
  };

  state = {
    loading: true,
    error: null,
    presentation: null,
    attempt: null,
    screen: null,
    currentQuestionIndex: 0,
    selectedAnswers: {},
    submitting: false,
    result: null,
    showLeaveModal: false,
    starting: false,
    attested: false,
    pollStartedAt: null,
    showLongWaitMessage: false,
    sprigAvailable: typeof window !== 'undefined' && typeof window.Sprig === 'function',
    gradingError: null,
    gradingAttempt: null,
    gradingResponses: null,
    gradingRetrying: false,
  };

  errorHeadingRef = React.createRef();

  introHeadingRef = React.createRef();

  awaitingHeadingRef = React.createRef();

  resultsHeadingRef = React.createRef();

  _pollTimeoutId = null;

  _pollGeneration = 0;

  // Where a polling failure returns the learner. The submit path keeps their
  // answers, so it falls back to the question screen; a cold resume never set
  // up a question flow, so it falls back to the intro.
  _pollFailureScreen = SCREENS.QUESTION;

  // Whether the current selectedAnswers are safely stored (restored from or written
  // to a draft). Drives the leave guard: unsaved answers warn, saved ones don't.
  _draftPersisted = false;

  componentDidMount() {
    this.loadPresentation();
    document.addEventListener("visibilitychange", this.handleVisibilityChange);
  }

  componentDidUpdate(prevProps, prevState) {
    const { error, screen, presentation } = this.state;

    if (error && !prevState.error) {
      document.title = tCommon("document_title_error");
    } else if (screen !== prevState.screen && presentation) {
      document.title = getDocumentTitle(screen, presentation);
    }

    const focusTarget = this.getFocusTarget(prevState);
    if (focusTarget) {
      clearTimeout(this._focusTimeout);
      this._focusTimeout = setTimeout(() => {
        const heading = focusTarget.current;
        if (heading && typeof heading.focus === "function") {
          heading.focus();
        }
      }, 100);
    }

    if (prevState.currentQuestionIndex !== this.state.currentQuestionIndex) {
      this.flushAnswerSideEffects();
    }
  }

  componentWillUnmount() {
    this.stopPolling();
    this.flushAnswerSideEffects();
    this.scheduleAnswerSideEffects.cancel();
    clearTimeout(this._focusTimeout);
    clearTimeout(this._retryWaitTimeout);
    document.removeEventListener("visibilitychange", this.handleVisibilityChange);
    this.teardownNavigationGuard();
  }

  getFocusTarget(prevState) {
    const { error, gradingError, gradingResponses, screen } = this.state;
    const gradingErrorShown = screen === SCREENS.GRADING_ERROR && (
      gradingError !== prevState.gradingError
      || screen !== prevState.screen
      || Boolean(gradingResponses) !== Boolean(prevState.gradingResponses)
    );

    // A retry that fails again keeps the screen but replaces gradingError, and the
    // retry button may disappear with it, so focus returns to the heading. It also
    // returns when polling after a retry fails and brings the screen back, and when the
    // answers finish loading and the error screen replaces the scoring screen.
    if ((error && !prevState.error) || (gradingErrorShown && gradingResponses)) {
      return this.errorHeadingRef;
    }

    if (gradingErrorShown) {
      return this.awaitingHeadingRef;
    }

    if (screen !== prevState.screen) {
      if (screen === SCREENS.INTRO) {
        return this.introHeadingRef;
      }

      // QUESTION focus is owned by the question screen (it focuses the first
      // choice on each question load), so the orchestrator does not focus here.

      if (screen === SCREENS.AWAITING_EVALUATION) {
        return this.awaitingHeadingRef;
      }

      if (screen === SCREENS.RESULTS) {
        return this.resultsHeadingRef;
      }
    }

    return null;
  }

  // Either preview flavor: nothing is persisted server-side, and the client
  // suppresses its own side effects (completion toast, analytics) so a preview
  // leaves no trace.
  isPreview = () => this.props.previewMode || this.props.learnerPreviewMode;

  // isPreview() says whether it is a preview; this says which type, since what Retry does differs.
  previewType = () => {
    if (this.props.learnerPreviewMode) {
      return PREVIEW_TYPES.LEARNER;
    }

    return this.props.previewMode ? PREVIEW_TYPES.AUTHOR : null;
  };

  emitTracking = (event, payload) => {
    if (this.isPreview()) {
      return;
    }

    track(event, payload);
  };

  trackingContext = () => {
    const { assessmentDefinitionId, sourceTrailId, sourceTrailUid, testOut } = this.props;

    return {
      assessmentDefinitionId,
      sourceTrailId,
      sourceTrailUid,
      testOut,
    };
  };

  // Drop a returning learner straight back into an unsubmitted attempt, bypassing
  // the intro. Resuming reuses the same question_order, so a randomized
  // (subset-drawn) assessment restores every saved answer instead of only the ones
  // that reappear in a new draw. A locked (passed/exhausted) assessment never resumes
  // a draft. The exception is an attempt still grading or failed at grading:
  // submitting the final attempt uses it up, so an exhausted learner returns to
  // that attempt. The server reports it, which also covers a reload with no
  // draft (another device).
  // A draft for a different attempt wins: it may be newer, unsubmitted work, and
  // grading the older attempt would show results and clear those answers. A draft
  // that points at the attempt the server says is still grading does not: failing
  // to re-read it must not dump the learner on the intro while that grade is in flight.
  resolveRecovery = async (presentation, locked) => {
    if (locked) {
      this.forgetDraft();
      if (locked === LOCKED_STATES.COMPLETE) {
        return null;
      }
    }

    const savedRecovery = locked ? null : await this.resumeSavedAttempt();
    if (savedRecovery) {
      return savedRecovery;
    }

    const serverRecovery = getServerRecovery(presentation);
    const draftAttemptId = this.draftsEnabled() ? loadDraft(this.getDraftId())?.attemptId : null;
    const draftBlocksServer = draftAttemptId && draftAttemptId !== getAttemptId(serverRecovery?.attempt);
    return draftBlocksServer ? null : serverRecovery;
  };

  applyRecovery = (recovery, presentation) => {
    switch (recovery?.action) {
      case RECOVERY_ACTIONS.QUESTIONS:
        this.setState({ loading: false, presentation }, () => this.enterAttempt(recovery.attempt, { resumed: true }));
        break;
      case RECOVERY_ACTIONS.AWAITING:
        this.setState({ loading: false, presentation }, () => void this.resumeAwaitingEvaluation(recovery.attempt));
        break;
      case RECOVERY_ACTIONS.GRADING_ERROR:
        this.setState({ loading: false, presentation }, () => this.handleGradingError(recovery.attempt));
        break;
      case RECOVERY_ACTIONS.RESULTS:
        this.setState(
          { loading: false, presentation, attempt: recovery.attempt },
          () => this.resumeGradedResult(recovery.attempt)
        );
        break;
      default:
        this.setState({ loading: false, presentation, screen: SCREENS.INTRO });
    }
  };

  loadPresentation = async () => {
    const { assessmentDefinitionId, previewMode, learnerPreviewMode, sourceTrailId } = this.props;

    this.setState({ loading: true, error: null });

    try {
      const presentation = await getPresentation(assessmentDefinitionId, {
        preview: previewMode,
        learnerPreview: learnerPreviewMode,
        contentType: sourceTrailId ? 'trail' : null,
        contentId: sourceTrailId || null,
      });
      const locked = getLockedState(presentation);

      this.emitTracking("assessment_framework_page_view", {
        ...this.trackingContext(),
        assessmentDefinitionVersion: presentation?.learner_context?.passing_version || null,
        lockedState: locked,
      });

      const recovery = await this.resolveRecovery(presentation, locked);
      this.applyRecovery(recovery, presentation);
    } catch (error) {
      this.setState({
        loading: false,
        error: getErrorMessage(error),
      });
    }
  };

  handleAttestChange = (checked) => {
    this.setState({ attested: checked });
  };

  // Start a fresh attempt from the intro. Resuming an unsubmitted attempt happens
  // automatically on load (see loadPresentation); the intro's CTA always starts new.
  handleStart = async () => {
    // A click can bypass the disabled button. Retake calls this from the results
    // screen, so the check applies only on the intro.
    if (this.state.screen === SCREENS.INTRO && !this.state.attested) {
      return;
    }

    this.setState({ starting: true });

    try {
      const attempt = await this.startNewAttempt();
      this.enterAttempt(attempt);
    } catch (error) {
      this.handleStartAttemptError(error);
    }
  };

  startNewAttempt = () => {
    const { assessmentDefinitionId, previewMode, learnerPreviewMode, sourceTrailId } = this.props;
    return startAttempt(assessmentDefinitionId, {
      preview: previewMode,
      learnerPreview: learnerPreviewMode,
      contentType: sourceTrailId ? 'trail' : null,
      contentId: sourceTrailId || null,
    });
  };

  // Enter the question screen for an attempt, restoring any saved answers and arming
  // the leave guard. Shared by a fresh start/retake and an on-load resume.
  enterAttempt = (attempt, { resumed = false } = {}) => {
    const selectedAnswers = this.restoreAnswersFromDraft(attempt);

    this.setState({
      attempt,
      screen: SCREENS.QUESTION,
      currentQuestionIndex: 0,
      selectedAnswers,
      result: null,
      showLeaveModal: false,
      starting: false,
    }, this.persistDraft);

    this.setupNavigationGuard();

    this.emitTracking("assessment_framework_attempt_started", {
      ...this.trackingContext(),
      attemptId: getAttemptId(attempt),
      attemptNumber: getAttemptNumber(attempt),
      resumed,
    });
  };

  // Fetch the stored attempt. An unsubmitted attempt returns to the questions.
  // One still grading resumes its poll; one that failed grading shows the error
  // screen; a finished one loads the result. Anything else drops the stale pointer.
  // A transient fetch error keeps the draft so a reload can retry. Returns null
  // to show the intro.
  resumeSavedAttempt = async () => {
    if (!this.draftsEnabled()) return null;

    const attemptId = loadDraft(this.getDraftId())?.attemptId;
    if (!attemptId) return null;

    let attempt;
    try {
      attempt = await getAssessmentAttempt(attemptId);
    } catch (error) {
      if (error?.railsFetchStatusCode === ERROR_CODES.NOT_FOUND || error?.railsFetchStatusCode === ERROR_CODES.FORBIDDEN) {
        clearDraft(this.getDraftId());
      }
      return null;
    }

    const recovery = recoveryForAttempt(attempt);
    if (recovery) {
      return recovery;
    }

    clearDraft(this.getDraftId());
    return null;
  };

  resumeAwaitingEvaluation = async (attempt) => {
    try {
      this.setState({
        attempt,
        result: null,
        showLeaveModal: false,
        starting: false,
      });

      const resultHref = assessmentResultHref(attempt.id);
      try {
        const result = await fetchFromHref(resultHref, this.assessmentFetchOptions());
        // Grading ended in error after the attempt was read. The result has no
        // poll or regrade link, so poll the attempt, which reports the error and
        // its Retry.
        const resumeFrom = getAttemptStatus(result) === ATTEMPT_STATUS.ERROR
          ? attempt
          : withAttemptPoll(result, attempt);
        this.beginAwaitingEvaluation(resumeFrom, SCREENS.INTRO);
      } catch (error) {
        this.beginAwaitingEvaluation(attempt, SCREENS.INTRO);
      }

      this.setupNavigationGuard();
    } catch (error) {
      console.error('Unexpected error in resumeAwaitingEvaluation:', error);
      this.setState({ screen: SCREENS.INTRO });
    }
  };

  resumeGradedResult = async (attempt) => {
    try {
      const result = await fetchFromHref(attempt.result_href, this.assessmentFetchOptions());
      this.showResults(result);
    } catch (error) {
      this.showToast(getErrorMessage(error));
      this.setState({ screen: SCREENS.INTRO, submitting: false });
    }
  };

  handleStartAttemptError = (error) => {
    const { presentation } = this.state;
    const isConflict = error?.railsFetchStatusCode === ERROR_CODES.CONFLICT;
    const conflictCode = isConflict ? error?.code : null;

    if (conflictCode === ATTEMPT_CONFLICT_CODES.ALREADY_PASSED) {
      this.setState({
        starting: false,
        presentation: {
          ...presentation,
          learner_context: {
            ...presentation.learner_context,
            passed: true,
          },
        },
      });
      return;
    }

    if (conflictCode === ATTEMPT_CONFLICT_CODES.ATTEMPTS_EXHAUSTED) {
      // Force the exhausted locked state. max_attempts can be null on the loaded
      // presentation (the server is the source of truth on exhaustion), so set a
      // concrete limit and meet it rather than relying on the loaded value.
      const limit = presentation?.max_attempts ?? (presentation?.learner_context?.attempt_count || 0) + 1;

      this.setState({
        starting: false,
        presentation: {
          ...presentation,
          max_attempts: limit,
          learner_context: {
            ...presentation.learner_context,
            attempt_count: limit,
          },
        },
      });
      return;
    }

    this.setState({ starting: false });
    this.showToast(getErrorMessage(error));
  };

  showToast = (message, variant = "error") => {
    window.dispatchEvent(
      new CustomEvent("showToast", {
        detail: { heading: message, variant },
      })
    );
  };

  hasAnswers = () => (
    this.state.screen === SCREENS.QUESTION &&
    Object.keys(this.state.selectedAnswers).length > 0
  );

  hasUnsavedAnswers = () => this.hasAnswers() && !this._draftPersisted;

  getDraftId = () => `${this.props.learnerId}|${this.props.assessmentDefinitionId}`;

  // Drafts are keyed on the learner; without a learnerId the key collapses to a
  // shared "null|<assessment>" bucket, so skip persistence entirely. Neither
  // preview flavor persists (a preview run must leave no trace).
  draftsEnabled = () => !this.isPreview() && Boolean(this.props.learnerId);

  restoreAnswersFromDraft = (attempt) => {
    if (!this.draftsEnabled()) return {};

    const draft = loadDraft(this.getDraftId());
    if (!draft) return {};

    return filterAnswersForAttempt(draft.responses || {}, attempt);
  };

  persistDraft = () => {
    if (!this.draftsEnabled()) {
      this._draftPersisted = false;
      return;
    }
    this._draftPersisted = saveDraft(
      this.getDraftId(),
      this.state.selectedAnswers,
      getAttemptId(this.state.attempt)
    );
  };

  emitPendingAnsweredTracking = () => {
    if (!this._pendingAnsweredTracking) {
      return;
    }

    this.emitTracking("assessment_framework_question_answered", {
      ...this.trackingContext(),
      ...this._pendingAnsweredTracking,
    });
    this._pendingAnsweredTracking = null;
  };

  // Open-ended answers update React state on every keystroke; localStorage writes
  // and analytics wait until typing settles. MCQ stays immediate (one click).
  scheduleAnswerSideEffects = debounce(() => {
    this.persistDraft();
    this.emitPendingAnsweredTracking();
  }, ANSWER_SIDE_EFFECT_DEBOUNCE_MS);

  flushAnswerSideEffects = () => {
    this.scheduleAnswerSideEffects.flush();
  };

  selectAnswer = (knowledgeCheckId, choiceId) => {
    if (this.state.submitting) {
      return;
    }

    const { attempt, currentQuestionIndex } = this.state;
    const knowledgeCheck = getKnowledgeChecks(attempt).find((check) => check.id === knowledgeCheckId);
    const debounceSideEffects = isSaqKnowledgeCheck(knowledgeCheck);

    if (debounceSideEffects) {
      this._pendingAnsweredTracking = {
        attemptId: getAttemptId(attempt),
        attemptNumber: getAttemptNumber(attempt),
        questionIndex: currentQuestionIndex,
      };
    }

    this.setState((prevState) => ({
      selectedAnswers: {
        ...prevState.selectedAnswers,
        [knowledgeCheckId]: choiceId,
      },
    }), debounceSideEffects ? this.scheduleAnswerSideEffects : this.persistDraft);

    if (debounceSideEffects) {
      return;
    }

    this.emitTracking("assessment_framework_question_answered", {
      ...this.trackingContext(),
      attemptId: getAttemptId(attempt),
      attemptNumber: getAttemptNumber(attempt),
      questionIndex: currentQuestionIndex,
    });
  };

  handleBack = () => {
    if (this.state.submitting) {
      return;
    }

    this.setState((prevState) => ({
      currentQuestionIndex: Math.max(0, prevState.currentQuestionIndex - 1),
    }));
  };

  handleNextOrSubmit = () => {
    const { attempt, currentQuestionIndex, submitting } = this.state;

    if (submitting) {
      return;
    }

    const totalQuestions = getKnowledgeChecks(attempt).length;

    if (currentQuestionIndex >= totalQuestions - 1) {
      this.handleSubmit();
      return;
    }

    this.setState((prevState) => ({
      currentQuestionIndex: prevState.currentQuestionIndex + 1,
    }));
  };

  handleSubmit = async ({ failureScreen = SCREENS.QUESTION } = {}) => {
    const { assessmentDefinitionId, previewMode, learnerPreviewMode, sourceTrailId } = this.props;
    const { attempt, selectedAnswers, submitting } = this.state;

    if (submitting) {
      return;
    }

    this.flushAnswerSideEffects();

    const attemptId = getAttemptId(attempt);
    const responses = buildSubmitResponses(attempt, selectedAnswers);

    this.setState({ submitting: true });

    this.emitTracking("assessment_framework_submitted", {
      ...this.trackingContext(),
      attemptId,
      attemptNumber: getAttemptNumber(attempt),
      responseCount: responses.length,
    });

    try {
      const result = await submitResults(assessmentDefinitionId, attemptId, responses, {
        preview: previewMode,
        learnerPreview: learnerPreviewMode,
        contentType: sourceTrailId ? 'trail' : null,
        contentId: sourceTrailId || null,
      });

      if (getAttemptStatus(result) === ATTEMPT_STATUS.AWAITING_EVALUATION) {
        this.beginAwaitingEvaluation(result, failureScreen);
        return;
      }

      this.showResults(result);
    } catch (error) {
      if (error?.railsFetchStatusCode === ERROR_CODES.CONFLICT && isGradedResultPayload(error)) {
        this.showResults(error);
        return;
      }

      this.setState({ submitting: false });
      this.showToast(getSubmitErrorMessage(error));
    }
  };

  assessmentFetchOptions = () => ({
    preview: this.props.previewMode,
    learnerPreview: this.props.learnerPreviewMode,
  });

  clearPollTimer = () => {
    if (this._pollTimeoutId) {
      clearTimeout(this._pollTimeoutId);
      this._pollTimeoutId = null;
    }
  };

  stopPolling = () => {
    this._pollGeneration += 1;
    this.clearPollTimer();
  };

  // The default message names the step that failed: submitting, unless polling began
  // after a retry, when the answers were already submitted and are saved.
  failPolling = (message = this.defaultPollFailureMessage()) => {
    this.stopPolling();
    this.setState(({ gradingAttempt, gradingResponses, selectedAnswers }) => ({
      submitting: false,
      screen: this._pollFailureScreen,
      // The grading error screen waits for its answers. If polling gave up before they were
      // known, the learner's own stand in, so Retry is not hidden behind the scoring screen.
      gradingResponses: this._pollFailureScreen === SCREENS.GRADING_ERROR && !gradingResponses
        ? buildErrorResponses(gradingAttempt, selectedAnswers)
        : gradingResponses,
      pollStartedAt: null,
      showLongWaitMessage: false,
    }));
    this.showToast(message);
  };

  defaultPollFailureMessage = () => (
    this._pollFailureScreen === SCREENS.GRADING_ERROR ? tErrors("unexpected_error") : tErrors("submit_failed")
  );

  beginAwaitingEvaluation = (result, failureScreen = SCREENS.QUESTION) => {
    this._pollFailureScreen = failureScreen;
    if (!hasPollTarget(result)) {
      if (getAttemptStatus(result) === ATTEMPT_STATUS.GRADED) {
        this.showResults(result);
      } else {
        this.failPolling();
      }
      return;
    }

    // The results screen can list answers while grading is still running: graded MCQs,
    // and SAQs whose score is still null.
    const hasPartialResults = getResultResponses(result).length > 0;

    if (hasPartialResults) {
      this.showResults(result);
    } else {
      this.setState({
        submitting: false,
        screen: SCREENS.AWAITING_EVALUATION,
      });
    }

    this.stopPolling();
    const generation = this._pollGeneration;
    this.setState({
      pollStartedAt: Date.now(),
      showLongWaitMessage: false,
    });
    this.schedulePoll(result.poll, generation);
  };

  schedulePoll = (poll, generation) => {
    if (generation !== this._pollGeneration) {
      return;
    }

    if (!isValidPoll(poll)) {
      this.failPolling();
      return;
    }

    this.clearPollTimer();
    this._pollTimeoutId = setTimeout(() => {
      this._pollTimeoutId = null;
      this.pollStatus(poll.href, generation);
    }, pollDelayMs(poll.retry_after_seconds));
  };

  handlePollFetchError = (error, generation, retryCount, retry) => {
    if (generation !== this._pollGeneration) {
      return;
    }

    if (isRetryablePollError(error)) {
      this.scheduleNetworkRetry(generation, retryCount, retry);
      return;
    }

    this.failPolling(getErrorMessage(error));
  };

  scheduleNetworkRetry = (generation, retryCount, retry) => {
    if (generation !== this._pollGeneration) {
      return;
    }

    if (retryCount >= NETWORK_RETRY_LIMIT) {
      this.failPolling(tErrors("connection_lost"));
      return;
    }

    const delayMs = (NETWORK_RETRY_BASE_MS * (2 ** retryCount)) + (Math.random() * NETWORK_RETRY_JITTER_MS);
    this.clearPollTimer();
    this._pollTimeoutId = setTimeout(() => {
      this._pollTimeoutId = null;
      // retry() re-enters pollStatus/loadGradedResults, which re-check the
      // generation on entry, so no guard is needed here (matches schedulePoll).
      retry();
    }, delayMs);
  };

  pollStatus = async (href, generation, retryCount = 0) => {
    if (generation !== this._pollGeneration) {
      return;
    }

    const { pollStartedAt, showLongWaitMessage } = this.state;
    if (
      !showLongWaitMessage
      && pollStartedAt != null
      && Date.now() - pollStartedAt > MAX_POLL_DURATION_MS
    ) {
      this.setState({ showLongWaitMessage: true });
    }

    let body;
    try {
      body = await fetchFromHref(href, this.assessmentFetchOptions());
    } catch (error) {
      this.handlePollFetchError(error, generation, retryCount, () => {
        this.pollStatus(href, generation, retryCount + 1);
      });
      return;
    }

    if (generation !== this._pollGeneration) {
      return;
    }

    if (isGradedResultPayload(body)) {
      this.loadGradedResults(body, generation);
      return;
    }

    if (getAttemptStatus(body) === ATTEMPT_STATUS.AWAITING_EVALUATION) {
      this.schedulePoll(body.poll, generation);
      return;
    }

    if (getAttemptStatus(body) === ATTEMPT_STATUS.ERROR) {
      this.handleGradingError(body);
      return;
    }

    this.failPolling();
  };

  // Re-renders once a second while Retry is waiting. The remaining time is always derived
  // from the fixed deadline, so a throttled background tab catches up instead of drifting.
  scheduleRetryWaitTick = () => {
    clearTimeout(this._retryWaitTimeout);

    if (this.state.screen !== SCREENS.GRADING_ERROR || retryWaitSeconds(this.state.gradingError) === 0) {
      return;
    }

    // Nothing counts a held wait down, so one timer to its deadline is enough to re-read it.
    const { gradingError } = this.state;
    const delayMs = isHeldError(gradingError)
      ? Math.min(gradingError.retryAvailableAt - Date.now(), MAX_TIMER_DELAY_MS)
      : MS_PER_SECOND;

    this._retryWaitTimeout = setTimeout(() => {
      this.forceUpdate(this.afterRetryWaitTick);
    }, delayMs);
  };

  afterRetryWaitTick = () => {
    if (retryWaitSeconds(this.state.gradingError) === 0) {
      void this.refreshGradingError();
    } else {
      this.scheduleRetryWaitTick();
    }
  };

  handleVisibilityChange = () => {
    if (document.visibilityState === "visible") {
      void this.refreshGradingError();
    }
  };

  // Polling stops on error, so the screen would never hear that ops released a held
  // attempt, or that the learner passed, ran out of attempts, or retried in another tab.
  // Re-read the attempt when the wait ends and when the tab comes back. A preview keeps
  // no server state to re-read.
  refreshGradingError = async () => {
    const { gradingAttempt, screen } = this.state;
    if (
      screen !== SCREENS.GRADING_ERROR || !gradingAttempt?.id || this.isPreview()
      || this._gradingRefreshInFlight || this._gradingRetryInFlight
    ) {
      return;
    }

    this._gradingRefreshInFlight = true;
    try {
      const attempt = await fetchFromHref(assessmentAttemptHref(gradingAttempt.id), this.assessmentFetchOptions());
      const stillShowing = this.state.screen === SCREENS.GRADING_ERROR
        && this.state.gradingAttempt === gradingAttempt
        && !this._gradingRetryInFlight;

      if (stillShowing) {
        await this.applyRefreshedAttempt(attempt);
      }
    } catch (error) {
      // A background check: keep what the screen already shows, unless the attempt is gone.
      if (error?.railsFetchStatusCode === ERROR_CODES.NOT_FOUND && this.state.gradingAttempt === gradingAttempt) {
        await this.leaveGoneAttempt();
      }
    } finally {
      this._gradingRefreshInFlight = false;
    }
  };

  applyRefreshedAttempt = async (attempt) => {
    const recovery = recoveryForAttempt(attempt);

    if (recovery?.action === RECOVERY_ACTIONS.AWAITING) {
      this.beginAwaitingEvaluation(await this.withAwaitingResults(attempt), SCREENS.GRADING_ERROR);
    } else if (recovery?.action === RECOVERY_ACTIONS.RESULTS) {
      await this.resumeGradedResult(attempt);
    } else if (
      recovery?.action === RECOVERY_ACTIONS.GRADING_ERROR
      && gradingErrorChanged(this.state.gradingError, gradingErrorFromAttempt(attempt))
    ) {
      this.handleGradingError(attempt, { announce: false });
    }
  };

  // Neither can be fixed with a new attempt, so there is nothing for the error screen to
  // offer: these send the learner to where the server says they now stand.
  routeAwayHandlers = () => ({
    [ATTEMPT_CONFLICT_CODES.ALREADY_PASSED]: this.routeAlreadyPassed,
    [ATTEMPT_CONFLICT_CODES.ATTEMPTS_EXHAUSTED]: this.routeAttemptsExhausted,
  });

  // A background refresh redraws the screen quietly: the learner was already told.
  handleGradingError = (attempt, { announce = true } = {}) => {
    this.stopPolling();

    const routeAway = this.routeAwayHandlers();
    const code = attempt?.error?.code;
    if (Object.hasOwn(routeAway, code)) {
      void routeAway[code](attempt.error);
      return;
    }

    const gradingError = gradingErrorFromAttempt(attempt);
    const message = announce ? gradingErrorToast(gradingError) : null;
    if (message) {
      this.showToast(message);
    }

    this.setState((state) => ({
      submitting: false,
      screen: SCREENS.GRADING_ERROR,
      gradingError,
      gradingAttempt: attempt,
      // The same attempt keeps its list on screen until the reload replaces it.
      gradingResponses: state.gradingAttempt?.id === attempt.id
        ? state.gradingResponses
        : answersOnScreen(state),
      pollStartedAt: null,
      showLongWaitMessage: false,
    }), () => {
      this.scheduleRetryWaitTick();
      void this.loadGradingResponses(attempt);
    });
  };

  // The result endpoint keeps every grade recorded before the failure, so the error
  // screen can show the graded MCQs and SAQs next to Retry. A presentation's
  // current_attempt carries no questions, so this is also what fills the list after a
  // reload. If the fetch fails the screen keeps the list built from client state.
  //
  // The attempt may have moved on since the error was read (a retry in another tab): a
  // graded result goes to the results screen, and one grading again resumes polling.
  loadGradingResponses = async (attempt) => {
    const result = await this.fetchOrNull(assessmentResultHref(attempt.id));

    if (this.state.gradingAttempt !== attempt || this.state.screen !== SCREENS.GRADING_ERROR) {
      return;
    }

    const status = getAttemptStatus(result);
    if (status === ATTEMPT_STATUS.GRADED) {
      this.showResults(result);
      return;
    }

    const grading = status === ATTEMPT_STATUS.AWAITING_EVALUATION;
    const listed = (status === ATTEMPT_STATUS.ERROR || grading) && result.responses?.length;

    // The answers are saved before polling resumes, since a poll that then fails comes back
    // to this screen. The learner's own answers stand in when the server's could not be fetched.
    this.setState(({ gradingResponses, selectedAnswers }) => ({
      gradingResponses: (listed && result.responses) || gradingResponses || buildErrorResponses(attempt, selectedAnswers),
    }), () => {
      if (grading && hasPollTarget(result)) {
        this.beginAwaitingEvaluation(result, SCREENS.GRADING_ERROR);
      }
    });
  };

  // The regrade response is the bare attempt, with no per-question results. The result
  // endpoint returns the already graded MCQs while the SAQs are pending, which lets the
  // results screen replace the generic scoring screen. Any failure keeps the bare attempt.
  withAwaitingResults = async (attempt) => {
    const result = await this.fetchOrNull(assessmentResultHref(attempt.id));

    return hasPollTarget(result) ? result : attempt;
  };

  fetchOrNull = async (href) => {
    try {
      return await fetchFromHref(href, this.assessmentFetchOptions());
    } catch {
      return null;
    }
  };

  // Refusals are told apart by the body's code. Its error string is untranslated
  // English, so it is never shown.
  handleRegradeRefusal = async (error) => {
    const status = error?.railsFetchStatusCode;
    const handlers = {
      ...this.routeAwayHandlers(),
      regrade_too_soon: this.handleRegradeTooSoon,
      not_retryable: this.handleRegradeNotRetryable,
      grading_in_progress: () => this.showToast(tErrors("grading_in_progress")),
    };

    if (status === ERROR_CODES.UNAUTHORIZED) {
      this.showToast(tErrors("unauthorized"));
    } else if (status === ERROR_CODES.FORBIDDEN) {
      this.showToast(tErrors("regrade_forbidden"));
    } else if (status === ERROR_CODES.NOT_FOUND) {
      await this.leaveGoneAttempt();
    } else if (Object.hasOwn(handlers, error?.code)) {
      await handlers[error.code](error);
    } else {
      this.showToast(tErrors("unexpected_error"));
    }
  };

  // retry_after_seconds replaces the local deadline: railsFetch cannot read Retry-After.
  handleRegradeTooSoon = (error) => {
    const seconds = Number(error.retry_after_seconds);
    if (!Number.isFinite(seconds) || seconds <= 0) {
      this.showToast(tErrors("regrade_too_soon"));
      return;
    }

    this.setState(({ gradingError }) => ({
      gradingError: { ...gradingError, retryAvailableAt: Date.now() + seconds * MS_PER_SECOND },
    }), this.scheduleRetryWaitTick);
  };

  // Usually another tab already retried: follow the attempt to wherever it is now.
  handleRegradeNotRetryable = async (error) => {
    const { gradingAttempt } = this.state;

    if (error.status === ATTEMPT_STATUS.AWAITING_EVALUATION && error.poll) {
      const awaiting = await this.withAwaitingResults({ id: gradingAttempt.id, poll: error.poll });
      this.beginAwaitingEvaluation(awaiting, SCREENS.GRADING_ERROR);
    } else if (error.status === ATTEMPT_STATUS.GRADED && error.result_href) {
      await this.resumeGradedResult({ result_href: error.result_href });
    } else {
      this.setState(({ gradingError }) => ({
        gradingError: { ...gradingError, retry: undefined, retryAvailableAt: null },
      }));
      this.showToast(tErrors("regrade_not_retryable"));
    }
  };

  // result_href is absent when the passing attempt no longer exists; the presentation
  // then shows the completed state. If the result cannot be fetched, the presentation says
  // the same, so the learner is not left on an intro that still offers to start.
  routeAlreadyPassed = async (error) => {
    const result = error.result_href ? await this.fetchOrNull(error.result_href) : null;
    if (result) {
      this.showResults(result);
      return;
    }

    this.forgetDraft();
    await this.loadPresentation();
  };

  // The attempt no longer exists on the server (recovering a merged user's records deletes
  // their attempts), so nothing here can be retried: show where the server now has the learner.
  leaveGoneAttempt = async () => {
    this.forgetDraft();
    await this.loadPresentation();
  };

  // The attempts were used up elsewhere after this screen loaded; the reloaded
  // presentation decides what to show (including a republish with a higher cap).
  routeAttemptsExhausted = async () => {
    this.showToast(tErrors("attempts_exhausted"));
    this.forgetDraft();
    await this.loadPresentation();
  };

  // The saved draft points at the errored attempt, so a reload would resume it and
  // land back on this routing.
  forgetDraft = () => {
    if (this.draftsEnabled()) {
      clearDraft(this.getDraftId());
    }
  };

  // A preview keeps no attempt to re-grade, so its error has no regrade_href. An author
  // preview posts the same answers again. A learner preview consumed its draw on submit,
  // so it starts over with a new one.
  handlePreviewRetry = () => {
    if (this.state.starting || this.state.submitting) {
      return;
    }

    if (this.props.learnerPreviewMode) {
      void this.handleStart();
    } else {
      void this.handleSubmit({ failureScreen: SCREENS.GRADING_ERROR });
    }
  };

  // A held error is not offered Retry while its wait runs. Its code never changes by itself,
  // so Retry comes back when the wait ends.
  getGradingRetryHandler = (gradingError) => {
    if (isHeldWait(gradingError)) {
      return null;
    }

    if (gradingError.retry) {
      return this.handleRetryGrading;
    }

    return this.isPreview() ? this.handlePreviewRetry : null;
  };

  handleRetryGrading = async () => {
    const { gradingError } = this.state;
    if (this._gradingRetryInFlight || !gradingError?.retry || retryWaitSeconds(gradingError) > 0) {
      return;
    }

    const { href, method } = gradingError.retry;
    this._gradingRetryInFlight = true;
    this.setState({ gradingRetrying: true });

    try {
      const result = await fetchFromHref(href, {
        method,
        ...this.assessmentFetchOptions(),
      });

      const status = getAttemptStatus(result);
      if (status === ATTEMPT_STATUS.ERROR) {
        this.handleGradingError(result);
        return;
      }

      if (status === ATTEMPT_STATUS.AWAITING_EVALUATION && hasPollTarget(result)) {
        this.beginAwaitingEvaluation(await this.withAwaitingResults(result), SCREENS.GRADING_ERROR);
      }
    } catch (error) {
      // A refused or dropped grade request is not a new grading failure. Unless the
      // refusal moves the learner on, the stored gradingError, including its retry
      // link, stays so the learner can try again.
      await this.handleRegradeRefusal(error);
    } finally {
      this._gradingRetryInFlight = false;
      this.setState({ gradingRetrying: false });
    }
  };

  loadGradedResults = async (attemptBody, generation, retryCount = 0) => {
    if (generation !== this._pollGeneration) {
      return;
    }

    const resultHref = attemptBody?.result_href;
    if (!resultHref) {
      this.failPolling();
      return;
    }

    let fullResults;
    try {
      fullResults = await fetchFromHref(resultHref, this.assessmentFetchOptions());
    } catch (error) {
      this.handlePollFetchError(error, generation, retryCount, () => {
        this.loadGradedResults(attemptBody, generation, retryCount + 1);
      });
      return;
    }

    if (generation !== this._pollGeneration) {
      return;
    }

    this.clearPollTimer();
    this.setState({ pollStartedAt: null, showLongWaitMessage: false });
    this.showResults(fullResults);
  };

  showResults = (result) => {
    const { attempt, presentation } = this.state;
    const nextPresentation = this.getPresentationWithResultContext(presentation, result);
    const resultSummary = deriveResultSummary(result, nextPresentation);
    const isFinalResult = getAttemptStatus(result) === ATTEMPT_STATUS.GRADED;

    if (isFinalResult) {
      if (resultSummary.passed) {
        this.queueAssessmentCompletionToast();
      }

      const trackingPayload = {
        ...this.trackingContext(),
        attemptId: result?.id || getAttemptId(attempt),
        attemptNumber: result?.attempt_number ?? getAttemptNumber(attempt),
        score_percent: resultSummary.scorePercent,
      };

      if (resultSummary.passed) {
        this.emitTracking("assessment_framework_completed", trackingPayload);
      } else {
        this.emitTracking("assessment_framework_failed", {
          ...trackingPayload,
          exhausted: !resultSummary.retakeAllowed,
        });
      }

      // The draft is the reload pointer while grading is still in flight. Clearing it
      // as soon as the first MCQ is scored sends a refresh back to the intro, and the
      // learner never sees the SAQ grade land.
      this.forgetDraft();
    }

    this.setState({
      presentation: nextPresentation,
      result,
      screen: SCREENS.RESULTS,
      showLeaveModal: false,
      submitting: false,
    });
  };

  queueAssessmentCompletionToast = () => {
    const { returnUrl, testOut } = this.props;

    // A preview run must leave no trace: no completion toast on the returned-to
    // trail, since the pass was never persisted.
    if (this.isPreview() || !returnUrl) {
      return;
    }

    try {
      const returnPath = new URL(returnUrl, window.location.origin).pathname.replace(/\/+$/, "");

      window.sessionStorage.setItem(
        ASSESSMENT_COMPLETION_TOAST_STORAGE_KEY,
        JSON.stringify({
          returnPath,
          message: tAssessment(assessmentCopyKey("success_toast", testOut)),
        })
      );
    } catch {
      // Storage can be unavailable under restrictive browser privacy settings.
    }
  };

  getPresentationWithResultContext = (presentation, result) => {
    const attemptCount = result?.attempts_used != null
      ? Number(result.attempts_used)
      : Math.max(
          presentation?.learner_context?.attempt_count || 0,
          result?.attempt_number || 0
        );

    return {
      ...presentation,
      learner_context: {
        ...presentation.learner_context,
        attempt_count: attemptCount,
        passed: result?.passed === true || presentation?.learner_context?.passed === true,
      },
    };
  };

  handleRetake = () => {
    const { result, presentation } = this.state;
    const resultSummary = deriveResultSummary(result, presentation);

    if (!resultSummary.retakeAllowed) {
      return;
    }

    this.emitTracking("assessment_framework_retake_started", {
      ...this.trackingContext(),
      previousAttemptId: result?.id || null,
      previousAttemptNumber: result?.attempt_number ?? null,
      score_percent: resultSummary.scorePercent,
    });

    this.handleStart();
  };

  setupNavigationGuard = () => {
    if (typeof window === "undefined" || this._navigationGuardActive) {
      return;
    }

    this._navigationGuardActive = true;
    window.history.pushState(null, "");
    window.addEventListener("popstate", this.handlePopState);
    window.addEventListener("beforeunload", this.handleBeforeUnload);
  };

  teardownNavigationGuard = () => {
    if (typeof window === "undefined" || !this._navigationGuardActive) {
      return;
    }

    this._navigationGuardActive = false;
    window.removeEventListener("popstate", this.handlePopState);
    window.removeEventListener("beforeunload", this.handleBeforeUnload);
  };

  handlePopState = () => {
    this.flushAnswerSideEffects();

    // The browser Back button popped our sentinel. If the learner has unsaved
    // answers to lose, re-push the sentinel and confirm; otherwise let the
    // navigation proceed.
    if (this.hasUnsavedAnswers()) {
      window.history.pushState(null, "");
      this.setState({ showLeaveModal: true });
      return;
    }

    // No answers to lose: the browser Back already popped the sentinel, so a single
    // step back reaches the trail.
    this.teardownNavigationGuard();
    this.navigateToTrail({ hadSentinel: false });
  };

  handleBeforeUnload = (event) => {
    this.flushAnswerSideEffects();

    if (!this.hasUnsavedAnswers()) {
      return undefined;
    }

    event.preventDefault();
    event.returnValue = "";
    return "";
  };

  // The leave modal only guards answers that are still at risk. On the results
  // screen, answers are submitted, so Return to Trail leaves without warning even
  // though the history sentinel remains until the learner intentionally leaves.
  handleReturnToTrail = () => {
    if (this.state.submitting) {
      return;
    }

    this.flushAnswerSideEffects();

    if (this._navigationGuardActive && this.hasUnsavedAnswers()) {
      this.setState({ showLeaveModal: true });
      return;
    }

    this.doReturnToTrail();
  };

  doReturnToTrail = () => {
    const hadSentinel = this._navigationGuardActive;
    this.teardownNavigationGuard();
    this.navigateToTrail({ hadSentinel });
  };

  launchFeedback = () => {
    if (typeof window.Sprig !== 'function') {
      return;
    }

    const { attempt, result, screen, presentation } = this.state;
    const { userTbid, userEmail, assessmentDefinitionId } = this.props;
    const isCompleted = result && result.status === ATTEMPT_STATUS.GRADED;
    const status = deriveAssessmentStatus(screen, isCompleted);

    const attributes = buildFeedbackAttributes(
      { attempt, result, presentation },
      { userTbid, userEmail, assessmentDefinitionId },
      isCompleted,
      status
    );

    const { toSet, toRemove } = partitionAttributes(attributes);

    updateSprigAttributes(toSet, toRemove);
    window.Sprig('track', 'open_feedback_form');

    track('assessment_framework_feedback', {
      assessmentDefinitionId,
      attemptId: attempt?.id || null,
      status: status || null,
    });
  };

  // Prefer an explicit server-provided trail URL over history arithmetic: a learner
  // who arrived via bookmark/deep link has no prior trail entry to go back to, so
  // history.go()/back() would strand them. Fall back to history only when no URL is
  // known — go(-2) to clear both the sentinel and the assessment page when the guard
  // was still active, otherwise a single step back.
  navigateToTrail = ({ hadSentinel = false } = {}) => {
    const { returnUrl } = this.props;

    if (returnUrl) {
      window.location.assign(returnUrl);
    } else if (hadSentinel) {
      window.history.go(-2);
    } else {
      window.history.back();
    }
  };

  handleStay = () => {
    this.setState({ showLeaveModal: false });
  };

  handleLeave = () => {
    this.setState({ showLeaveModal: false });
    this.doReturnToTrail();
  };

  renderScreen() {
    const { loading, error, presentation, screen, starting, attested } = this.state;

    if (loading) {
      return (
        <LoadingScreen
          scope={`${ASSESSMENT_FRAMEWORK_SCOPE}.common`}
        />
      );
    }

    if (error) {
      return (
        <ErrorScreen
          message={error}
          onRetry={this.loadPresentation}
          onBackToTrail={this.handleReturnToTrail}
          scope={`${ASSESSMENT_FRAMEWORK_SCOPE}.pages.error`}
          headingRef={this.errorHeadingRef}
        />
      );
    }

    if (screen === SCREENS.GRADING_ERROR) {
      const { gradingError, gradingResponses, gradingRetrying, starting, submitting } = this.state;

      // The error and the answers appear together: until the answers are known, the scoring
      // screen keeps showing, so the list does not pop in under the heading.
      if (!gradingResponses) {
        return <AwaitingEvaluationScreen headingRef={this.awaitingHeadingRef} />;
      }

      const retrying = gradingRetrying || starting || submitting;

      return (
        <AssessmentFrameworkResultsScreen
          result={{ status: ATTEMPT_STATUS.ERROR, responses: gradingResponses }}
          presentation={presentation}
          header={(
            <GradingErrorScreen
              gradingError={gradingError}
              previewType={this.previewType()}
              retrying={retrying}
              onRetry={this.getGradingRetryHandler(gradingError)}
              onBackToTrail={this.handleReturnToTrail}
              headingRef={this.errorHeadingRef}
            />
          )}
          reevaluating={retrying}
          onRetake={this.handleRetake}
          onReturnToTrail={this.handleReturnToTrail}
        />
      );
    }

    if (screen === SCREENS.INTRO) {
      return (
        <AssessmentFrameworkIntroScreen
          presentation={presentation}
          lockedState={getLockedState(presentation)}
          onStart={this.handleStart}
          starting={starting}
          attested={attested}
          onAttestChange={this.handleAttestChange}
          headingRef={this.introHeadingRef}
          testOut={this.props.testOut}
          showUnassignedNotice={this.props.showUnassignedNotice}
          showPreviewNotice={this.isPreview()}
        />
      );
    }

    if (screen === SCREENS.QUESTION) {
      const { attempt, currentQuestionIndex, selectedAnswers, submitting } = this.state;
      const knowledgeChecks = getKnowledgeChecks(attempt);
      const currentQuestion = knowledgeChecks[currentQuestionIndex] || null;

      return (
        <AssessmentFrameworkQuestionScreen
          knowledgeCheck={currentQuestion}
          currentQuestionIndex={currentQuestionIndex}
          totalQuestions={knowledgeChecks.length}
          selectedId={currentQuestion ? selectedAnswers[currentQuestion.id] : null}
          submitting={submitting}
          onSelectAnswer={(choiceId) => this.selectAnswer(currentQuestion.id, choiceId)}
          onBack={this.handleBack}
          onNextOrSubmit={this.handleNextOrSubmit}
        />
      );
    }

    if (screen === SCREENS.AWAITING_EVALUATION) {
      return (
        <AwaitingEvaluationScreen
          showLongWaitMessage={this.state.showLongWaitMessage}
          headingRef={this.awaitingHeadingRef}
        />
      );
    }

    if (screen === SCREENS.RESULTS) {
      const { result } = this.state;

      return (
        <AssessmentFrameworkResultsScreen
          result={result}
          presentation={presentation}
          onRetake={this.handleRetake}
          onReturnToTrail={this.handleReturnToTrail}
          retaking={starting}
          headingRef={this.resultsHeadingRef}
          testOut={this.props.testOut}
          onFeedbackClick={this.launchFeedback}
          showFeedbackButton={this.state.sprigAvailable}
        />
      );
    }

    return null;
  }

  render() {
    const { presentation, screen, showLeaveModal, gradingResponses } = this.state;
    // The grading error is laid out like the results once its answers are in; until then the
    // scoring screen stands in and keeps its own layout.
    const resultsLayout = screen === SCREENS.RESULTS
      || (screen === SCREENS.GRADING_ERROR && Boolean(gradingResponses));
    const showTitle = screen === SCREENS.QUESTION
      || screen === SCREENS.AWAITING_EVALUATION
      || screen === SCREENS.RESULTS
      || screen === SCREENS.GRADING_ERROR;
    const pageClassName = [
      "th-agentforce-assessment__assessment-framework-page",
      resultsLayout ? "th-agentforce-assessment__assessment-framework-page--results" : null,
    ].filter(Boolean).join(" ");

    return (
      <>
        <a href="#main-content" className="th-sr-only th-sr-only--focusable">
          {tNav("skip_to_content")}
        </a>
        <AssessmentFrameworkTopBar
          title={presentation?.title}
          showTitle={showTitle}
          onReturnToTrail={this.handleReturnToTrail}
        />
        <main id="main-content" className={pageClassName}>
          {this.renderScreen()}
        </main>
        <LeaveAssessmentModal
          open={showLeaveModal}
          onStay={this.handleStay}
          onLeave={this.handleLeave}
        />
      </>
    );
  }
}
